import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import NotificationBell from './NotificationBell';
import { Car, LogOut, Menu, Type, Sun, Moon, DollarSign, ShieldAlert, Archive, Target, Mail, Globe, Wifi, WifiOff, Keyboard } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import { APP_VERSION } from '../utils/version';


const FONT_SIZES = [
  { label: 'Τ', scale: '0.85' },
  { label: 'Μ', scale: '0.93' },
  { label: 'Κ', scale: '1' },
  { label: 'Μγ', scale: '1.08' },
];

export default function GlobalHeader({ currentUser, onLogout, onOpenMenu }) {
  const navigate = useNavigate();
  const [fontSize, setFontSize] = useState(() => localStorage.getItem('pm_font') || '1');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('pm_theme') === 'dark');
  const [showCashDialog, setShowCashDialog] = useState(false);
  const [showPanicConfirm, setShowPanicConfirm] = useState(false);
  const [openingCash, setOpeningCash] = useState(false);
  const [openingBarrier, setOpeningBarrier] = useState(false);
  const [closingBarrier, setClosingBarrier] = useState(false);
  const revenueCheckRef = useRef(null);
  const [smtpOk, setSmtpOk] = useState(null);
  const [epsilonOnline, setEpsilonOnline] = useState(null);
  // v1.0.9.15 — barrier permission gate (hide header quick-buttons if user lacks it)
  const [canOpenCloseBarrier, setCanOpenCloseBarrier] = useState(false);

  // Fetch service statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const [smtpRes, offlineRes] = await Promise.all([
          apiFetch(`/api/settings/smtp-health`).catch(() => null),
          apiFetch(`/api/offline/status`).catch(() => null),
        ]);
        if (smtpRes?.ok) {
          const d = await smtpRes.json();
          setSmtpOk(d.configured || d.status === 'ok');
        }
        if (offlineRes?.ok) {
          const d = await offlineRes.json();
          setEpsilonOnline(d.connectivity?.online ?? false);
        }
      } catch {}
    };
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 30000);
    return () => clearInterval(interval);
  }, []);

  // v1.0.9.15 — Compute barrier open/close permission from tenant settings.
  // Admin: always yes. user/manager: check matrix.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser) return;
      if (currentUser.role === 'admin') {
        if (!cancelled) setCanOpenCloseBarrier(true);
        return;
      }
      try {
        const r = await apiFetch(`/api/settings`);
        if (!r.ok) return;
        const d = await r.json();
        const map = currentUser.role === 'manager'
          ? (d.manager_menu_permissions || {})
          : (d.user_menu_permissions || {});
        if (!cancelled) setCanOpenCloseBarrier(Boolean(map.barrier_open_close));
      } catch { /* ignore — treat as no permission */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Revenue notification polling
  useEffect(() => {
    const checkRevenue = async () => {
      try {
        const res = await apiFetch(`/api/operations/revenue-notifications`);
        if (res.ok) {
          const data = await res.json();
          if (data.notifications && data.notifications.length > 0) {
            data.notifications.forEach(n => {
              toast.success(n.message, {
                duration: 8000,
                icon: <Target size={18} className="text-emerald-600" />,
                description: `€${n.revenue} / €${n.target}`,
              });
            });
            // Mark as read
            await apiFetch(`/api/operations/revenue-notifications/read`, { method: 'POST' });
            // Browser push notification if supported
            if ('Notification' in window && Notification.permission === 'granted') {
              data.notifications.forEach(n => {
                new Notification('Parkingevo - Έσοδα', { body: n.message, icon: '/favicon.ico' });
              });
            }
          }
        }
      } catch {}
    };
    
    checkRevenue();
    revenueCheckRef.current = setInterval(checkRevenue, 60000); // Every minute
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    return () => clearInterval(revenueCheckRef.current);
  }, []);

  const handleFontChange = (scale) => {
    setFontSize(scale);
    localStorage.setItem('pm_font', scale);
    document.documentElement.style.fontSize = `${parseFloat(scale) * 16}px`;
  };

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('pm_theme', next ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', next);
  };

  // Apply dark class on mount
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, []);

  const handleOpenCashRegister = async () => {
    setOpeningCash(true);
    try {
      await apiFetch(`/api/operations/cash-register/open`, { method: 'POST' });
      toast.success('Ταμειακή ανοίχτηκε');
    } catch { toast.error('Σφάλμα σύνδεσης POS'); }
    setOpeningCash(false);
    setShowCashDialog(false);
  };

  const handleOpenBarrier = async () => {
    setOpeningBarrier(true);
    try {
      const response = await apiFetch(`/api/barrier/open`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Αποτυχία');
      toast.success(data.message || 'Η μπάρα άνοιξε!');
    } catch (error) {
      toast.error(error.message || 'Σφάλμα μπάρας');
    } finally {
      setOpeningBarrier(false);
    }
  };

  const handleCloseBarrier = async () => {
    setClosingBarrier(true);
    try {
      const response = await apiFetch(`/api/barrier/close`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Αποτυχία');
      toast.success(data.message || 'Η μπάρα έκλεισε!');
    } catch (error) {
      toast.error(error.message || 'Σφάλμα μπάρας');
    } finally {
      setClosingBarrier(false);
    }
  };

  const handlePanicButton = async () => {
    setOpeningCash(true);
    try {
      // Open cash register
      await apiFetch(`/api/operations/cash-register/open`, { method: 'POST' });
      // Send silent security alert
      await apiFetch(`/api/operations/security-alert`, { method: 'POST' });
      toast.success('Ταμειακή ανοίχτηκε');
    } catch { toast.error('Σφάλμα'); }
    setOpeningCash(false);
    setShowPanicConfirm(false);
    setShowCashDialog(false);
  };

  React.useEffect(() => {
    const savedFont = localStorage.getItem('pm_font');
    if (savedFont) document.documentElement.style.fontSize = `${parseFloat(savedFont) * 16}px`;
    const savedTheme = localStorage.getItem('pm_theme');
    if (savedTheme === 'dark') document.documentElement.classList.add('dark');
  }, []);

  return (
    <>
    <header className="bg-[#004C98] dark:bg-slate-900 text-white py-3 px-4 shadow-lg sticky top-0 z-50 w-full overflow-hidden" data-testid="global-header">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 min-w-0 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="header-brand">
            {/* v1.0.9.29 · new branding: white transparent mark on the blue header */}
            <img
              src="/logo-mark-white.png"
              alt="ParkingEvo"
              className="h-9 w-auto select-none"
              draggable="false"
              data-testid="header-logo-mark"
            />
            <div className="hidden sm:flex flex-col leading-tight" data-testid="header-brand-block">
              <span className="text-lg font-extrabold tracking-tight">
                ParkingEvo
                <span className="ml-1.5 text-[10px] font-medium text-blue-200/70 align-middle" data-testid="header-version">
                  v{APP_VERSION}
                </span>
              </span>
              <span className="text-[11px] text-blue-100 font-medium" data-testid="header-current-user-name">
                {currentUser?.name || currentUser?.username || 'Χρήστης'}
              </span>
              {currentUser?.previous_login && (
                <span className="text-[10px] text-blue-200" data-testid="header-previous-session">
                  Προηγ. σύνδεση: {new Date(currentUser.previous_login).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  {' · '}
                  {currentUser?.previous_logout ? new Date(currentUser.previous_logout).toLocaleString('el-GR', { hour: '2-digit', minute: '2-digit' }) : 'ενεργή'}
                </span>
              )}
            </div>
          </div>
          {/* Cash Register Button */}
          <Button onClick={() => setShowCashDialog(true)} variant="ghost" className="text-white hover:bg-white/10 h-8 px-2 text-xs gap-1" data-testid="cash-register-btn">
            <Archive size={16} />
            <span className="hidden sm:inline">Συρτάρι</span>
          </Button>
          {/* Service Status Indicators */}
          <div className="flex items-center gap-1 ml-1" data-testid="header-status-indicators">
            {smtpOk !== null && (
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${smtpOk ? 'bg-white/15 text-green-300' : 'bg-red-500/30 text-red-200'}`}
                title={smtpOk ? 'Email OK' : 'Email Offline'} data-testid="header-status-email">
                <Mail size={11} />
                <span className="hidden sm:inline">Email</span>
                <span className={`w-1.5 h-1.5 rounded-full ${smtpOk ? 'bg-green-400' : 'bg-red-400 animate-pulse'}`} />
              </div>
            )}
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-white/15 text-green-300"
              title="Internet OK" data-testid="header-status-internet">
              <Globe size={11} />
              <span className="hidden sm:inline">Internet</span>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            </div>
            {epsilonOnline !== null && (
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${epsilonOnline ? 'bg-white/15 text-green-300' : 'bg-red-500/30 text-red-200'}`}
                title={epsilonOnline ? 'Epsilon OK' : 'Epsilon Offline'} data-testid="header-status-epsilon">
                {epsilonOnline ? <Wifi size={11} /> : <WifiOff size={11} />}
                <span className="hidden sm:inline">Epsilon</span>
                <span className={`w-1.5 h-1.5 rounded-full ${epsilonOnline ? 'bg-green-400' : 'bg-red-400 animate-pulse'}`} />
              </div>
            )}
          </div>
          <div className="hidden xl:flex items-center gap-1 ml-2" data-testid="header-barrier-buttons">
            {canOpenCloseBarrier && (<>
            <Button
              onClick={handleOpenBarrier}
              disabled={openingBarrier}
              variant="ghost"
              className="h-7 px-2 bg-green-600/90 hover:bg-green-700 text-white rounded-full text-[10px] font-semibold gap-1 disabled:opacity-60"
              data-testid="header-open-barrier-button"
            >
              <span>{openingBarrier ? '...' : 'Άνοιγμα Μπάρας'}</span>
            </Button>
            <Button
              onClick={handleCloseBarrier}
              disabled={closingBarrier}
              variant="ghost"
              className="h-7 px-2 bg-red-600/90 hover:bg-red-700 text-white rounded-full text-[10px] font-semibold gap-1 disabled:opacity-60"
              data-testid="header-close-barrier-button"
            >
              <span>{closingBarrier ? '...' : 'Κλείσιμο Μπάρας'}</span>
            </Button>
            </>)}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 min-w-0">
          {/* Keyboard Shortcuts Indicator */}
          <div className="relative group hidden sm:block">
            <Button variant="ghost" className="text-white hover:bg-white/10 h-8 w-8 p-0" data-testid="shortcuts-indicator">
              <Keyboard size={16} />
            </Button>
            <div className="absolute right-0 top-full mt-1 w-52 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-3 hidden group-hover:block z-[60]" data-testid="shortcuts-tooltip">
              <p className="text-[10px] font-bold text-white/60 uppercase mb-2 tracking-wider">Συντομεύσεις</p>
              <div className="space-y-1.5 text-xs text-white">
                <div className="flex justify-between"><span>Νέα Είσοδος</span><kbd className="bg-white/15 px-1.5 rounded font-mono text-[10px]">F1</kbd></div>
                <div className="flex justify-between"><span>Έξοδος</span><kbd className="bg-white/15 px-1.5 rounded font-mono text-[10px]">F2</kbd></div>
                <div className="flex justify-between"><span>Πελάτες</span><kbd className="bg-white/15 px-1.5 rounded font-mono text-[10px]">F3</kbd></div>
                <div className="flex justify-between"><span>Ταμείο</span><kbd className="bg-white/15 px-1.5 rounded font-mono text-[10px]">F4</kbd></div>
                <div className="flex justify-between"><span>Dashboard</span><kbd className="bg-white/15 px-1.5 rounded font-mono text-[10px]">F5</kbd></div>
              </div>
            </div>
          </div>
          {/* Menu Button - first, next to bell */}
          <Button
            onClick={onOpenMenu || (() => window.dispatchEvent(new CustomEvent('pm-open-menu')))}
            variant="ghost"
            className="text-white hover:bg-white/10 h-8 px-2 gap-1"
            data-testid="global-menu-button"
          >
            <Menu size={20} />
            <span className="text-xs font-bold">Μενού</span>
          </Button>
          <NotificationBell />
          {/* Theme Toggle — hidden on mobile to save header width */}
          <Button onClick={toggleTheme} variant="ghost" className="text-white hover:bg-white/10 h-8 w-8 p-0 hidden md:inline-flex" data-testid="theme-toggle">
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
          {/* Font Size Selector — hidden on mobile (accessible via Menu) */}
          <div className="hidden md:flex items-center gap-0.5 bg-white/15 rounded-lg px-1 py-0.5" data-testid="font-size-selector">
            <Type size={12} className="text-white/70 mr-0.5" />
            {FONT_SIZES.map(f => (
              <button
                key={f.scale}
                onClick={() => handleFontChange(f.scale)}
                className={`px-1.5 py-0.5 text-[10px] rounded font-bold transition-colors ${fontSize === f.scale ? 'bg-white text-[#004C98]' : 'text-white/60 hover:text-white'}`}
                data-testid={`font-size-${f.label}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button
            onClick={onLogout}
            variant="ghost"
            className="text-white hover:bg-white/10 h-8 px-2 hidden sm:flex items-center gap-1 text-xs"
            data-testid="global-logout-button"
          >
            <LogOut size={16} />
            <span>Έξοδος</span>
          </Button>
        </div>
      </div>
    </header>

    {/* Cash Register Dialog */}
    <Dialog open={showCashDialog} onOpenChange={setShowCashDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="text-emerald-600" size={20} /> Ταμειακή — Συρτάρι
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Button onClick={handleOpenCashRegister} disabled={openingCash}
            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold" data-testid="open-cash-confirm">
            <Archive size={20} className="mr-2" /> {openingCash ? 'Άνοιγμα...' : 'Άνοιγμα Συρταριού'}
          </Button>
          <div className="border-t pt-3">
            <Button onClick={() => setShowPanicConfirm(true)}
              variant="outline" className="w-full h-10 border-red-300 text-red-700 hover:bg-red-50 text-sm" data-testid="panic-btn">
              <ShieldAlert size={16} className="mr-2" /> Κουμπί Κινδύνου
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Panic Confirmation */}
    <Dialog open={showPanicConfirm} onOpenChange={setShowPanicConfirm}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-red-700 flex items-center gap-2">
            <ShieldAlert size={20} /> Επιβεβαίωση Κινδύνου
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">Θα ανοίξει η ταμειακή και θα σταλεί κρυφό μήνυμα ασφαλείας στις καταχωρημένες επαφές.</p>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowPanicConfirm(false)} className="flex-1">Ακύρωση</Button>
          <Button onClick={handlePanicButton} disabled={openingCash}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="panic-confirm">
            {openingCash ? 'Αποστολή...' : 'Επιβεβαίωση'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
